import React, { ReactNode, useState } from 'react';

export interface InfoField {
  label: string;
  value: ReactNode;
}

export interface InfoColumn {
  fields: InfoField[];
}

interface EntityOverviewProps {
  avatar?: ReactNode;
  avatarUrl?: string;
  avatarAlt?: string;
  title: string;
  tags?: ReactNode;
  subtitle: string;
  description?: ReactNode;
  infoColumns?: InfoColumn[];
  className?: string;
  defaultCollapsed?: boolean;
}

export const EntityOverview: React.FC<EntityOverviewProps> = ({
  avatar,
  avatarUrl,
  avatarAlt = 'Entity',
  title,
  tags,
  subtitle,
  description,
  infoColumns,
  className = '',
  defaultCollapsed = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [isHovering, setIsHovering] = useState(false);
  const showAvatar = avatar || avatarUrl;

  // Limit to max 4 columns
  const displayColumns = infoColumns ? infoColumns.slice(0, 4) : [];
  
  // Determine grid columns based on number of columns
  const gridColsClass = {
    1: 'md:grid-cols-1',
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
  }[displayColumns.length] || 'md:grid-cols-3';

  const hasInfoColumns = infoColumns && infoColumns.length > 0;

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    // Blur the button after click to remove focus state
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  return (
    <div 
      className={`bg-blue-200 rounded-md shadow-lg border border-gray-300 p-4 sm:p-6 relative ${className}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4">
        {showAvatar && (
          <div className="flex-shrink-0">
            {avatar ? (
              avatar
            ) : avatarUrl ? (
              <img
                src={avatarUrl}
                alt={avatarAlt}
                className="w-28 h-28 rounded-lg object-cover"
              />
            ) : null}
          </div>
        )}
        <div className="flex-1 min-w-0 w-full text-center sm:text-left">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-1">
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
                  {title}
                </h1>
                {tags && <div className="flex flex-wrap gap-2">{tags}</div>}
              </div>
              <p className="text-sm text-gray-600">{subtitle}</p>
            </div>
          </div>
          {description && (
            <div className="text-sm text-gray-700 leading-relaxed flex justify-center sm:justify-start">
              {description}
            </div>
          )}
        </div>
      </div>
      {hasInfoColumns && (
        <div className="border-t border-gray-200 mt-4 sm:mt-6 bg-white/70 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 rounded-b-md relative">
          <button
            onClick={() => {
              toggleCollapse();
            }}
            className={`absolute left-1/2 -translate-x-1/2 -top-4 z-10 bg-blue-100 px-3 py-1.5 rounded-full border border-gray-200 transition-all duration-200 opacity-100 shadow-md sm:opacity-0 ${
              isHovering ? 'sm:opacity-100 sm:shadow-md' : ''
            } hover:bg-blue-50 focus:outline-none focus:ring-2`}
            aria-label={isCollapsed ? 'Expand details' : 'Collapse details'}
          >
            <svg 
              className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${
                isCollapsed ? 'rotate-180' : ''
              }`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 20 20"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <div 
            className={`overflow-hidden transition-all duration-300 ${
              isCollapsed ? 'max-h-0' : 'max-h-[2000px]'
            }`}
          >
            <div className="pt-6 px-4 sm:px-6 pb-4 sm:pb-6">
              <div className={`grid grid-cols-1 ${gridColsClass} md:divide-x divide-gray-200 gap-6`}>
                {displayColumns.map((column, columnIndex) => (
                  <div 
                    key={columnIndex} 
                    className={`space-y-4 ${
                      columnIndex === 0 
                        ? 'md:pr-6' 
                        : columnIndex === displayColumns.length - 1 
                        ? 'md:pl-6' 
                        : 'md:px-6'
                    }`}
                  >
                    {column.fields.map((field, fieldIndex) => (
                      <div key={fieldIndex}>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">
                          {field.label}
                        </span>
                        {typeof field.value === 'string' ? (
                          <span className="text-sm text-gray-900">{field.value}</span>
                        ) : (
                          <div>{field.value}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
